import type { SearchResult } from '@liniya/api-client';
import { useState, type ReactNode } from 'react';

import { Modal } from '@/components/Modal';
import { UserSearch } from '@/features/search/UserSearch';
import { notify } from '@liniya/client-core';
import { useCreateChannel, useCreateGroup, useOpenDirectChat } from '@liniya/client-core';

type Mode = 'direct' | 'group' | 'channel';

interface Props {
  onOpenChat: (chatId: string) => void;
  /**
   * `fab` is the floating control in the corner of the chat list; `icon` is a
   * plain header button. The trigger differs, the dialog behind it does not.
   */
  variant?: 'icon' | 'fab';
  children: ReactNode;
}

/** Turn a title into the slug a public channel is addressed by. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9а-я\s-]/gi, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

export function NewChatMenu({ onOpenChat, variant = 'icon', children }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('direct');
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [selected, setSelected] = useState<SearchResult[]>([]);

  const openDirect = useOpenDirectChat();
  const createGroup = useCreateGroup();
  const createChannel = useCreateChannel();

  const close = () => {
    setOpen(false);
    setQuery('');
    setTitle('');
    setSlug('');
    setSelected([]);
    setMode('direct');
  };

  const pick = async (result: SearchResult) => {
    if (mode === 'direct') {
      if (result.type !== 'user') return;
      try {
        const chat = await openDirect.mutateAsync(result.id);
        onOpenChat(chat.id);
        close();
      } catch {
        notify('Не удалось открыть диалог.', 'error');
      }
      return;
    }
    if (result.type !== 'user') return;
    setSelected((current) =>
      current.some((item) => item.id === result.id) ? current : [...current, result],
    );
    setQuery('');
  };

  const submitGroup = async () => {
    try {
      const chat = await createGroup.mutateAsync({
        title: title.trim(),
        member_user_ids: selected.map((item) => item.id),
      });
      onOpenChat(chat.id);
      close();
    } catch {
      notify('Не удалось создать группу.', 'error');
    }
  };

  const submitChannel = async () => {
    try {
      const chat = await createChannel.mutateAsync({
        title: title.trim(),
        slug: slug.trim() || slugify(title),
      });
      onOpenChat(chat.id);
      close();
    } catch {
      notify('Не удалось создать канал. Возможно, адрес занят.', 'error');
    }
  };

  return (
    <>
      <button
        className={variant === 'fab' ? 'new-chat-fab' : 'icon-btn dark'}
        type="button"
        aria-label="Новый чат"
        title="Новый чат"
        onClick={() => setOpen(true)}
      >
        {children}
      </button>

      {open ? (
        <Modal title="Новый чат" onClose={close}>
          <div className="tabs" role="tablist" aria-label="Тип чата">
            {(
              [
                ['direct', 'Диалог'],
                ['group', 'Группа'],
                ['channel', 'Канал'],
              ] as Array<[Mode, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                className={mode === value ? 'tab active' : 'tab'}
                onClick={() => setMode(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {mode !== 'direct' ? (
            <div className="field">
              <label htmlFor="new-chat-title">Название</label>
              <input
                id="new-chat-title"
                className="input"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  if (mode === 'channel') setSlug(slugify(event.target.value));
                }}
                maxLength={120}
              />
            </div>
          ) : null}

          {mode === 'channel' ? (
            <div className="field">
              <label htmlFor="new-chat-slug">Адрес канала</label>
              <input
                id="new-chat-slug"
                className="input"
                value={slug}
                onChange={(event) => setSlug(slugify(event.target.value))}
                maxLength={80}
              />
              <span className="hint">Публичный адрес, по которому канал можно найти.</span>
            </div>
          ) : null}

          {mode !== 'channel' ? (
            <div className="field">
              <label htmlFor="new-chat-search">
                {mode === 'direct' ? 'Кому написать' : 'Добавить участников'}
              </label>
              <input
                id="new-chat-search"
                className="input"
                placeholder="Имя, username или email"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
              />
              <UserSearch query={query} onPick={pick} busy={openDirect.isPending} />
            </div>
          ) : null}

          {selected.length > 0 ? (
            <p className="hint">
              Участники: {selected.map((item) => item.title).join(', ')}
            </p>
          ) : null}

          {mode !== 'direct' ? (
            <div className="modal-actions">
              <button type="button" className="secondary-btn" onClick={close}>
                Отмена
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={
                  !title.trim() ||
                  createGroup.isPending ||
                  createChannel.isPending ||
                  (mode === 'group' && selected.length === 0)
                }
                onClick={mode === 'group' ? submitGroup : submitChannel}
              >
                Создать
              </button>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
